import { useEffect, useState } from "react";
import { RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatPrice } from "@/lib/store";
import { TRADE_CATEGORIES, tradeModels, DEDUCTIONS, NO_STORAGE } from "@/lib/tradeCatalog";
import { ModelPicker } from "@/components/site/ModelPicker";
import { chip } from "@/components/site/OrderSpecFields";

const input = "w-full min-w-0 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-right text-sm text-white outline-none focus:border-white/40";
// Aceita "1.900", "1900", "1900,50" e "1.900,50": o último separador só é decimal se tiver 1 ou 2 dígitos depois.
const num = (v) => {
  const s = String(v ?? "").replace(/[^\d.,]/g, "");
  const last = Math.max(s.lastIndexOf("."), s.lastIndexOf(","));
  const isDec = last >= 0 && s.length - last - 1 <= 2;
  return Number(isDec ? `${s.slice(0, last).replace(/[.,]/g, "")}.${s.slice(last + 1)}` : s.replace(/[.,]/g, "")) || 0;
};
const toDraft = (obj = {}) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, v ? String(v) : ""]));

const MoneyField = ({ label, value, onChange, testid }) => (
  <label className="flex items-center gap-2 text-sm">
    <span className="w-40 shrink-0 text-zinc-300">{label}</span>
    <span className="text-zinc-400">R$</span>
    <input data-testid={testid} inputMode="decimal" value={value || ""} placeholder="0" onChange={(e) => onChange(e.target.value)} className={input} />
  </label>
);

// Bônus fixo (igual para todos os aparelhos) quando o cliente marca "Aparelho comprado na RC iPhones".
const TradeBonusField = () => {
  const [amount, setAmount] = useState("");
  useEffect(() => {
    api.get("/trade-bonus").then((r) => setAmount(r?.data?.amount ? String(r.data.amount) : "")).catch(() => setAmount(""));
  }, []);
  const save = async () => {
    try {
      const { data } = await api.put("/admin/trade-bonus", { amount: num(amount) });
      toast.success(data?.amount > 0 ? `Bônus de ${formatPrice(data.amount)} salvo` : "Bônus desligado");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div data-testid="trade-bonus-editor" className="mt-4 rounded-2xl border border-emerald-400/30 bg-emerald-400/[0.05] p-4">
      <p className="text-sm font-semibold text-white">Bônus para aparelho comprado na RC</p>
      <p className="mt-1 text-xs text-zinc-400">Valor somado à avaliação quando o cliente marca que comprou o aparelho com você. Vale para todos os aparelhos. Deixe 0 para desligar.</p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <span className="text-sm text-zinc-400">R$</span>
        <input data-testid="trade-bonus-input" inputMode="decimal" value={amount} placeholder="0" onChange={(e) => setAmount(e.target.value)} className={`${input} max-w-[160px]`} />
        <button type="button" data-testid="trade-bonus-save" onClick={save} className="rounded-full bg-white px-5 py-2 text-sm font-semibold text-black transition-transform hover:scale-[1.02]">Salvar bônus</button>
      </div>
    </div>
  );
};

// Tabela de valores da troca: quanto a loja paga por modelo/GB e quanto desconta por problema.
export const TradePricesTable = () => {
  const [category, setCategory] = useState("iphone");
  const [model, setModel] = useState("");
  const [values, setValues] = useState({});
  const [deductions, setDeductions] = useState({});
  const [saved, setSaved] = useState([]);
  const load = () => api.get("/trade-prices").then((r) => setSaved(Array.isArray(r?.data) ? r.data : [])).catch(() => setSaved([]));
  useEffect(() => { load(); }, []);

  const models = tradeModels(category);
  const entry = models.find((m) => m.name === model);
  const storages = entry?.storages?.length ? entry.storages : [NO_STORAGE];
  const open = (name, cat = category) => {
    const doc = saved.find((s) => s.model === name);
    setCategory(cat);
    setModel(name);
    setValues(toDraft(doc?.values));
    setDeductions(toDraft(doc?.deductions));
  };

  const save = async () => {
    try {
      const body = { model, category, values: Object.fromEntries(storages.map((s) => [s, num(values[s])]).filter(([, v]) => v > 0)), deductions: Object.fromEntries(DEDUCTIONS.map(([k]) => [k, num(deductions[k])])) };
      await api.put("/admin/trade-prices", body);
      toast.success(`Valores do ${model} salvos`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const remove = async (name) => {
    await api.delete("/admin/trade-prices", { params: { model: name } }).catch(() => null);
    toast.success("Valores removidos");
    if (name === model) {
      setValues({});
      setDeductions({});
    }
    load();
  };

  return (
    <section data-testid="admin-trade-prices" className="mt-8 rounded-[24px] border border-white/10 bg-[#111113] p-5 sm:p-6">
      <div className="flex items-center gap-2.5"><RefreshCw className="h-5 w-5 text-white" /><h2 className="font-display text-xl font-bold">Valores da troca</h2></div>
      <p className="mt-2 text-sm text-zinc-400">Escolha o aparelho, coloque quanto você paga em cada GB e os descontos. Deixe 0 para não descontar. O cliente vê a avaliação estimada na página Troca.</p>
      <TradeBonusField />
      <div className="mt-4 flex flex-wrap gap-2">
        {TRADE_CATEGORIES.map((c) => (
          <button type="button" key={c.id} data-testid={`trade-prices-category-${c.id}`} onClick={() => { setCategory(c.id); setModel(""); }} className={chip(category === c.id)}>{c.label}</button>
        ))}
      </div>
      <ModelPicker testid="trade-prices-model" value={model} onChange={(n) => open(n)} options={models.map((m) => m.name)} selectOnly groupOf={(o) => models.find((m) => m.name === o)?.group} placeholder="Toque para escolher o aparelho" className={`${input} mt-3 text-left`} />
      {model && (
        <div data-testid="trade-prices-editor" className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-400">Quanto você paga</p>
            {storages.map((s) => (
              <MoneyField key={s} label={s === NO_STORAGE ? "Valor" : s} testid={`trade-price-${s}`} value={values[s]} onChange={(v) => setValues((x) => ({ ...x, [s]: v }))} />
            ))}
          </div>
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-400">Descontos</p>
            {DEDUCTIONS.map(([k, label]) => (
              <MoneyField key={k} label={label} testid={`trade-deduction-input-${k}`} value={deductions[k]} onChange={(v) => setDeductions((x) => ({ ...x, [k]: v }))} />
            ))}
          </div>
          <button type="button" data-testid="trade-prices-save" onClick={save} className="w-fit rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-black transition-transform hover:scale-[1.02]">Salvar valores</button>
        </div>
      )}
      {saved.length > 0 && (
        <div className="mt-6">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-400">Já cadastrados ({saved.length})</p>
          <ul data-testid="trade-prices-saved" className="mt-2 divide-y divide-white/10">
            {saved.map((s) => (
              <li key={s.model} className="flex items-center justify-between gap-3 py-2 text-sm">
                <button type="button" data-testid="trade-prices-saved-item" onClick={() => open(s.model, s.category)} className="min-w-0 truncate text-left text-zinc-200 hover:text-white">
                  {s.model} <span className="text-zinc-400">· {Object.entries(s.values || {}).map(([k, v]) => `${k === NO_STORAGE ? "" : `${k} `}${formatPrice(v)}`).join(" · ")}</span>
                </button>
                <button type="button" onClick={() => remove(s.model)} aria-label="Remover" className="rounded-full p-2 text-zinc-400 hover:bg-white/10 hover:text-white"><Trash2 className="h-4 w-4" /></button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};
